import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./Navbar";

const EMAIL = "vmdpproject@gmail.com";
const PIVA = "10990261215";
const UPDATED = "10 luglio 2026";
const ACCENT = "#10b981";

/* ─── Sezione testo legale ─── */
function Section({ n, title, children }) {
  return (
    <div
      style={{
        marginBottom: "2.75rem",
        paddingBottom: "2.75rem",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "1rem",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            fontWeight: 500,
            color: ACCENT,
            background: "rgba(16,185,129,0.1)",
            border: "1px solid rgba(16,185,129,0.28)",
            borderRadius: "6px",
            padding: "3px 9px",
            flexShrink: 0,
          }}
        >
          {String(n).padStart(2, "0")}
        </span>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(1.3rem, 2.5vw, 1.6rem)",
            color: "var(--color-text)",
            letterSpacing: "-0.01em",
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <div
        style={{
          color: "var(--color-text-2)",
          fontSize: "1rem",
          lineHeight: 1.85,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const linkStyle = {
  color: ACCENT,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
};
const ulStyle = {
  paddingLeft: "1.25rem",
  margin: "0.75rem 0",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

export default function VeyroPrivacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Stesso documento servito sotto /veyro e /veyro-trainer: i link
     di ritorno seguono l'app da cui si arriva. */
  const { pathname } = useLocation();
  const base = pathname.startsWith("/veyro-trainer") ? "/veyro-trainer" : "/veyro";
  const appName = base === "/veyro-trainer" ? "Veyro Trainer" : "Veyro";

  return (
    <>
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: "var(--color-bg)", minHeight: "100vh" }}
      >
        {/* ── Hero ── */}
        <section
          style={{
            paddingTop: "68px",
            background:
              "linear-gradient(180deg, rgba(16,185,129,0.08) 0%, transparent 100%)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <div className="container" style={{ padding: "4rem 1.75rem 3rem" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: ACCENT,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: "1rem",
              }}
            >
              {`// ${appName.toLowerCase()} · privacy policy`}
            </span>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
                marginBottom: "1rem",
              }}
            >
              Privacy{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Policy
              </span>
            </h1>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: "var(--color-muted)",
              }}
            >
              Veyro · Veyro Trainer · Ultimo aggiornamento: {UPDATED}
            </p>
          </div>
        </section>

        {/* ── Contenuto ── */}
        <div className="container" style={{ padding: "3rem 1.75rem 5rem" }}>
          {/* Intro card */}
          <div
            style={{
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.22)",
              backdropFilter: "blur(12px)",
              borderRadius: "var(--radius-lg)",
              padding: "1.5rem 1.75rem",
              marginBottom: "3rem",
              color: "var(--color-text-2)",
              fontSize: "1rem",
              lineHeight: 1.8,
            }}
          >
            La presente Informativa descrive come vengono trattati i dati
            personali degli utenti delle applicazioni{" "}
            <strong style={{ color: "var(--color-text)" }}>Veyro</strong>{" "}
            (app per clienti) e{" "}
            <strong style={{ color: "var(--color-text)" }}>Veyro Trainer</strong>{" "}
            (app per personal trainer), di seguito congiuntamente le "App", ai
            sensi del Regolamento (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003.
          </div>

          <Section n={1} title="Titolare del Trattamento">
            <strong style={{ color: "var(--color-text)" }}>VMDProject</strong>
            <br />
            Sede: Napoli, Italia
            <br />
            P.IVA: {PIVA}
            <br />
            Email:{" "}
            <a href={`mailto:${EMAIL}`} style={linkStyle}>
              {EMAIL}
            </a>
            <br />
            <br />
            Per qualsiasi questione relativa al trattamento dei tuoi dati
            personali puoi scrivere direttamente al Titolare all'indirizzo
            email indicato.
          </Section>

          <Section n={2} title="Dati Personali che Raccogliamo">
            <strong style={{ color: "var(--color-text)" }}>
              a) Dati dell'account
            </strong>
            <ul style={ulStyle}>
              <li>Nome e cognome, indirizzo email, password (in forma cifrata)</li>
              <li>Numero di telefono (se fornito)</li>
              <li>Ruolo (cliente o personal trainer)</li>
              <li>Foto profilo / avatar (se caricata)</li>
            </ul>

            <strong style={{ color: "var(--color-text)" }}>
              b) Dati relativi alla salute e al fitness
            </strong>{" "}
            (categorie particolari, art. 9 GDPR) — trattati solo previo tuo
            consenso esplicito:
            <ul style={ulStyle}>
              <li>Obiettivi di fitness, sesso, data di nascita, altezza, livello di attività</li>
              <li>Peso corporeo, misure corporee</li>
              <li>Foto di check-in dei progressi fisici</li>
              <li>Log di allenamento (esercizi, serie, ripetizioni, carichi, RPE)</li>
              <li>Piani alimentari e alimenti registrati</li>
            </ul>

            <strong style={{ color: "var(--color-text)" }}>
              c) Dati di pagamento
            </strong>
            <br />
            Elaborati tramite{" "}
            <strong style={{ color: "var(--color-text)" }}>Stripe</strong>. Non
            memorizziamo i dati completi della carta; per i personal trainer,
            i dati necessari ai payout avvengono tramite Stripe Connect.
            <br />
            <br />
            <strong style={{ color: "var(--color-text)" }}>
              d) Dati tecnici e di utilizzo
            </strong>
            <ul style={ulStyle}>
              <li>Identificativo del dispositivo, sistema operativo, versione app</li>
              <li>Token per le notifiche push</li>
              <li>Dati di diagnostica e crash (Sentry)</li>
              <li>Eventi di utilizzo pseudonimi (PostHog)</li>
            </ul>

            <strong style={{ color: "var(--color-text)" }}>
              e) Dati da login social
            </strong>
            <br />
            Se accedi con Apple o Google, riceviamo nome ed email associati a
            quell'account, secondo le tue impostazioni di condivisione presso
            il provider.
          </Section>

          <Section n={3} title="Finalità e Basi Giuridiche">
            <ul style={ulStyle}>
              <li>Creazione e gestione dell'account, erogazione del servizio — esecuzione del contratto.</li>
              <li>Gestione di schede, piani alimentari, progressi e coaching — consenso esplicito (dati salute).</li>
              <li>Elaborazione pagamenti e payout — esecuzione del contratto e obblighi fiscali.</li>
              <li>Notifiche push relative al servizio — legittimo interesse / consenso.</li>
              <li>Sicurezza, prevenzione abusi, diagnostica crash — legittimo interesse.</li>
              <li>Statistiche di utilizzo e miglioramento del prodotto — consenso.</li>
            </ul>
            Puoi revocare il consenso in qualsiasi momento (vedi Sezione 5),
            senza pregiudicare la liceità dei trattamenti effettuati prima
            della revoca.
          </Section>

          <Section n={4} title="Fornitori Terzi (Responsabili del Trattamento)">
            Ci avvaliamo di fornitori terzi che trattano i dati per nostro conto:
            <ul style={ulStyle}>
              <li><strong style={{ color: "var(--color-text)" }}>Supabase</strong> — database, autenticazione, storage.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Stripe</strong> — pagamenti e payout.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Expo (EAS)</strong> — notifiche push, distribuzione app.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Sentry</strong> — diagnostica errori/crash.</li>
              <li><strong style={{ color: "var(--color-text)" }}>PostHog</strong> — analisi di utilizzo.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Apple / Google</strong> — login social, distribuzione store.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Open Food Facts</strong> — ricerca alimenti (solo la query è inviata, senza dati identificativi).</li>
            </ul>
            Alcuni fornitori possono trattare dati al di fuori dello Spazio
            Economico Europeo; in tal caso il trasferimento avviene sulla base
            delle Clausole Contrattuali Standard approvate dalla Commissione
            Europea.
          </Section>

          <Section n={5} title="Diritti dell'Utente (GDPR)">
            Ai sensi degli articoli 15–22 del GDPR hai il diritto di:
            <ul style={ulStyle}>
              <li><strong style={{ color: "var(--color-text)" }}>Accesso:</strong> ottenere conferma che siano trattati dati che ti riguardano e riceverne copia.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Rettifica:</strong> richiedere la correzione di dati inesatti.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Cancellazione:</strong> richiedere la cancellazione dei tuoi dati ("diritto all'oblio").</li>
              <li><strong style={{ color: "var(--color-text)" }}>Limitazione:</strong> richiedere la limitazione del trattamento in determinate circostanze.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Portabilità:</strong> ricevere i tuoi dati in formato strutturato e leggibile da macchina.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Opposizione:</strong> opporti al trattamento basato sul legittimo interesse.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Revoca del consenso:</strong> in qualsiasi momento, senza pregiudizio per la liceità del trattamento precedente.</li>
            </ul>
            Puoi esercitare questi diritti scrivendo a{" "}
            <a href={`mailto:${EMAIL}`} style={linkStyle}>
              {EMAIL}
            </a>{" "}
            oppure eliminando il tuo account direttamente dall'app (sezione
            Profilo). Hai inoltre il diritto di proporre reclamo al{" "}
            <strong style={{ color: "var(--color-text)" }}>
              Garante per la Protezione dei Dati Personali
            </strong>{" "}
            (
            <a
              href="https://www.garanteprivacy.it"
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              garanteprivacy.it
            </a>
            ).
          </Section>

          <Section n={6} title="Periodo di Conservazione">
            <ul style={ulStyle}>
              <li><strong style={{ color: "var(--color-text)" }}>Dati account e fitness:</strong> per tutta la durata dell'account, cancellati entro 30 giorni dalla chiusura.</li>
              <li><strong style={{ color: "var(--color-text)" }}>Dati di pagamento/fatturazione:</strong> per il periodo previsto dalla normativa fiscale (in genere 10 anni).</li>
              <li><strong style={{ color: "var(--color-text)" }}>Dati di diagnostica/analytics:</strong> in forma pseudonimizzata, fino a 14 mesi.</li>
            </ul>
          </Section>

          <Section n={7} title="Minori">
            Le App non sono destinate a minori di 16 anni. Non raccogliamo
            consapevolmente dati di minori senza il consenso di chi esercita
            la responsabilità genitoriale. Se ritieni che un minore ci abbia
            fornito dati, contattaci per la rimozione.
          </Section>

          <Section n={8} title="Notifiche Push, Permessi e Cookie">
            Le App possono richiedere l'accesso a{" "}
            <strong style={{ color: "var(--color-text)" }}>notifiche</strong>{" "}
            (allenamenti, abbonamenti, aggiornamenti) e alla{" "}
            <strong style={{ color: "var(--color-text)" }}>galleria foto</strong>{" "}
            (per le foto di check-in dei progressi, accesso solo alle immagini
            selezionate). Puoi disattivare questi permessi in qualsiasi
            momento dalle impostazioni del dispositivo.
            <br />
            <br />
            Le App e il sito web associato utilizzano esclusivamente
            cookie/identificatori tecnici necessari al funzionamento (es.
            sessione di autenticazione) e, previo consenso, identificatori
            pseudonimi di analisi tramite PostHog. Non utilizziamo cookie di
            profilazione pubblicitaria di terze parti. Puoi gestire il
            consenso analytics dalle impostazioni dell'app o del dispositivo
            (es. App Tracking Transparency su iOS).
          </Section>

          <Section n={9} title="Trasferimento Dati Extra-UE">
            Alcuni fornitori terzi utilizzati (Supabase, Stripe, Sentry,
            PostHog) possono trattare dati in paesi extra-UE. Tali
            trasferimenti avvengono nel rispetto delle garanzie previste dal
            GDPR (Clausole Contrattuali Standard o meccanismi equivalenti).
          </Section>

          <Section n={10} title="Modifiche alla Presente Informativa">
            VMDProject si riserva il diritto di aggiornare questa Informativa
            in qualsiasi momento. In caso di modifiche sostanziali ne daremo
            avviso tramite l'app o via email. La data in alto indica l'ultimo
            aggiornamento.
          </Section>

          <Section n={11} title="Contatti">
            Per qualsiasi domanda relativa a questa Privacy Policy:
            <br />
            <br />
            <strong style={{ color: "var(--color-text)" }}>VMDProject</strong> —
            Napoli, Italia
            <br />
            P.IVA: {PIVA}
            <br />
            Email:{" "}
            <a href={`mailto:${EMAIL}`} style={linkStyle}>
              {EMAIL}
            </a>
          </Section>
        </div>

        {/* ─── Footer ─── */}
        <footer
          style={{
            borderTop: "1px solid var(--color-border)",
            padding: "2rem 0",
          }}
        >
          <div
            className="container"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <Link
              to={base}
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "var(--color-text)",
                textDecoration: "none",
              }}
            >
              ← {appName}
            </Link>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              <Link
                to={`${base}/policy`}
                style={{ fontSize: "0.9rem", color: ACCENT, textDecoration: "none" }}
              >
                Privacy
              </Link>
              <Link
                to={`${base}/terms`}
                style={{
                  fontSize: "0.9rem",
                  color: "var(--color-muted)",
                  textDecoration: "none",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "var(--color-text)")}
                onMouseLeave={(e) => (e.target.style.color = "var(--color-muted)")}
              >
                Termini
              </Link>
            </div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: "var(--color-muted)",
              }}
            >
              © {new Date().getFullYear()} VMDProject
            </span>
          </div>
        </footer>
      </motion.main>
    </>
  );
}
